import InputField from "../../UI/Inputs/InputField";
import InputSelect, { SelectOption } from "../../UI/Inputs/InputSelect";
import Modal from "../../UI/Modal";
import Button from "../../UI/Button";
import { VehicleNames } from "../../../utils/GeneralTypes";
import { useCallback, useEffect, useState } from "react";
import { getReportReasons, reportVehicle } from "../../../utils/fetcher";
import { moduleToSelectOptions } from "../FindVehicleFilter/utils";
import { useForm } from "react-hook-form";
import { Success, Warning } from "../../Icons";
import style from "../../../pages/VehiclePage/index.module.css";
import { useTranslation } from "../../../hooks/useTranslation";

const ReportVehicle = ({ setIsWarningModalActive, vehicleNames, vehicleID }: ComponentPropsType) => {
  const { locale, siteLang } = useTranslation();

  const [componentData, setComponentData] = useState<{
    isFetching: boolean;
    data: {
      reasons: SelectOption[];
    };
  }>({
    isFetching: true,
    data: {
      reasons: [],
    },
  });

  const { handleSubmit, watch, register, setValue } = useForm({
    defaultValues: {
      report_reason_id: "no_option",
      comment: "",
    },
  });

  const watchedInputs = watch();

  const [submitState, setSubmitState] = useState<{
    isSubmitting: boolean;
    error: string;
    success: boolean;
  }>({
    isSubmitting: false,
    error: "",
    success: false,
  });

  useEffect(() => {
    (async () => {
      const data: { reasons: SelectOption[] } = { reasons: [] };
      await getReportReasons()
        .then((res) => {
          data.reasons = moduleToSelectOptions(res.data.reasons, [["reason", ""], "id", ""]).options;
        })
        .catch((error) => {});
      setComponentData({
        isFetching: false,
        data,
      });
    })();
  }, []);

  const onReportVehicleHandler = useCallback(
    async (data: any) => {
      setSubmitState({
        isSubmitting: true,
        error: "",
        success: false,
      });
      await reportVehicle(vehicleNames.single, vehicleID, data)
        .then(() => {
          setSubmitState({
            isSubmitting: false,
            success: true,
            error: "",
          });
        })
        .catch((error) => {
          setSubmitState({
            isSubmitting: false,
            success: false,
            error: error?.response?.data?.message || locale("error_occured"),
          });
        });
    },
    [vehicleNames, vehicleID, locale],
  );

  const onCloseResponseModal = useCallback(
    (success: boolean) => {
      if (success) {
        setIsWarningModalActive(false);
        setSubmitState({ isSubmitting: false, error: "", success: false });
      } else setSubmitState({ isSubmitting: false, error: "", success: false });
    },
    [setIsWarningModalActive],
  );

  return (
    <>
      {(submitState.success || submitState.error) && (
        <Modal onClose={() => onCloseResponseModal(submitState.success)}>
          <div className={style.modal}>
            <div className={style.modal_response}>
              {submitState.success ? (
                <Success width="50" height="50" color="#35b545" />
              ) : (
                <Warning width="50" height="50" color="#dc0000" />
              )}
              <p style={{ margin: "10px 0 0" }}>
                {submitState.success ? locale("reported_successfuly") : submitState.error}
              </p>
            </div>
          </div>
        </Modal>
      )}
      {!submitState.success && !submitState.error && (
        <Modal onClose={() => setIsWarningModalActive(false)}>
          <div className={style.modal}>
            <p className={style.modal_title}>
              {locale("report_this")} {siteLang === "ar" && ` هذه ال`}
              {siteLang === "en" && ""}
              {locale(vehicleNames.single).toLocaleLowerCase()}
            </p>
            <form onSubmit={handleSubmit(onReportVehicleHandler)}>
              <div className={style.modal_inputs}>
                <InputSelect
                  props={{
                    ...register("report_reason_id"),
                    value: watchedInputs.report_reason_id,
                  }}
                  selectOptions={componentData.data.reasons}
                  setValue={setValue}
                />

                <InputField type="textarea" isSecondary={true} placeholder={locale("add_comment")} />
              </div>
              <div className={style.modal_ctas}>
                <Button
                  styleType="primary_black"
                  buttonType="submit"
                  disabled={submitState.isSubmitting || watchedInputs.report_reason_id === "no_option"}
                  isLoading={submitState.isSubmitting}
                >
                  {locale("send")}
                </Button>
              </div>
            </form>
          </div>
        </Modal>
      )}
    </>
  );
};

export default ReportVehicle;

type ComponentPropsType = {
  setIsWarningModalActive: CallableFunction;
  vehicleNames: VehicleNames;
  vehicleID: string;
};
